import { cloneDeep } from 'lodash';
import {
  AuthMethod,
  ConnectionType,
  ConnectionTypeName,
  MongoConnectionScheme,
  MongoReadPreference,
  S3Connection,
  SalesforceEnvironment,
  SomeConnection,
} from '../connection.models';
import { OAuthName } from './connections-authentication.config';

export interface ConnectionTypeModel {
  defaults?: Partial<SomeConnection>;
  auth?: OAuthName;
  name?: string;
  description?: string;
  type?: string;
  icon_url?: string;
  auth_data?: any;
  button?: {
    test?: boolean;
    create?: boolean;
    authenticate?: boolean;
  };
}

export interface Connections {
  [key: string]: ConnectionTypeModel;
}

export interface ConnectionGroup {
  connections: ConnectionType[];
  name: string;
}

const sortByName = (a, b) => (a.name > b.name ? 1 : -1);

export const defaultConnections: Connections = {
  vertica: {
    defaults: {
      tunnel_type: 'direct',
      port: 5433,
    },
  },
  azuresynapseanalytics: {
    defaults: {
      tunnel_type: 'direct',
      port: 1433,
      ssl: true,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  db2: {
    defaults: {
      tunnel_type: 'direct',
      port: 50001,
      ssl: true,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  as400: {
    defaults: {
      tunnel_type: 'direct',
      port: 50001,
      ssl: true,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  intercom: {
    auth: OAuthName.intercom,
  },
  restapi: {},
  googlecloudpostgres: {
    defaults: {
      tunnel_type: 'direct',
      port: 5432,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  alloy: {
    defaults: {
      tunnel_type: 'direct',
      port: 5432,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  googlecloudspanner: {
    auth: OAuthName.googleCloudSpanner,
  },
  adwords: {
    auth: OAuthName.adwords,
  },
  analytics: {
    auth: OAuthName.analytics,
  },
  ga4: {
    defaults: {},
  },
  bigquery: {
    defaults: {
      region: 'gcloud::us-central1',
    },
  },
  bigqueryv2: {
    defaults: {
      region: 'gcloud::us-central1',
    },
  },
  bingads: {
    auth: OAuthName.bingAds,
  },
  googlecloudsql: {
    defaults: {
      tunnel_type: 'direct',
      port: 3306,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  gs: {},
  hana: {},
  hdfs: {},
  herokupostgres: {
    defaults: {
      tunnel_type: 'direct',
      port: 5432,
      ssl: true,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  facebookadsinsights: {
    auth: OAuthName.facebookAdsInsights,
  },
  facebookads: {
    auth: OAuthName.facebookAds,
  },
  mongo: {
    defaults: {
      tunnel_type: 'direct',
      port: 27017,
      ssl: false,
      read_preference: MongoReadPreference.primary,
      ssh_port: 22,
      ssh_username: 'xplenty',
      connection_scheme: MongoConnectionScheme.dnsSeedList,
    },
  },
  mysql: {
    defaults: {
      tunnel_type: 'direct',
      port: 3306,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  postgres: {
    defaults: {
      tunnel_type: 'direct',
      port: 5432,
      ssl: true,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  oracle: {
    defaults: {
      tunnel_type: 'direct',
      port: 1521,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  rackspace: {},
  redshift: {
    defaults: {
      tunnel_type: 'direct',
      port: 5439,
      ssl: true,
      region: 'amazon-web-services::us-east-1',
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  s3: {
    defaults: {
      region: 'amazon-web-services::us-east-1',
    } as Partial<S3Connection>,
  },
  salesforce: {
    auth: OAuthName.salesforce,
    defaults: {
      environment: SalesforceEnvironment.production,
      auth_method: AuthMethod.salesforce,
    },
  },
  salesforcesoap: {
    defaults: {},
  },
  segment: {
    defaults: {
      tunnel_type: 'direct',
      port: 5439,
      ssl: true,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  sftp: {
    defaults: {
      auth_method: AuthMethod.password,
      tunnel_type: 'direct',
      port: 22,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  ftps: {
    defaults: {
      tunnel_type: 'direct',
      port: 21,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  softlayer: {},
  sqlserver: {
    defaults: {
      tunnel_type: 'direct',
      port: 1433,
      ssl: true,
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  swift: {
    defaults: {
      auth_method: AuthMethod.swauth,
    },
  },
  netsuite: {
    defaults: {
      database: 'NetSuite.com',
      port: 1708,
      role_id: 0,
    },
  },
  azureblobstorage: {},
  pipedrive: {
    auth: OAuthName.pipedrive,
  },
  snowflake: {
    defaults: {
      tunnel_type: 'direct',
      region: 'snowflake::us-west-2',
      ssh_port: 22,
      ssh_username: 'xplenty',
    },
  },
  xero: {
    auth: OAuthName.xero,
  },
  linkedin: {
    auth: OAuthName.linkedin,
  },
  instagram: {
    auth: OAuthName.instagram,
  },
  googlesheets: {
    auth: OAuthName.googleSheets,
  },
  googledrive: {
    auth: OAuthName.googleDrive,
  },
  youtube: {
    auth: OAuthName.youtube,
  },
  shopify: {
    auth: OAuthName.shopify,
    defaults: {
      auth_method: AuthMethod.shopify,
    },
  },
  tiktokads: {
    auth: OAuthName.tiktokAds,
  },

  athena: {
    defaults: {
      region: 'amazon-web-services::us-east-1',
    },
  },
  curl: {},
  marketingcloud: {
    defaults: {},
  },
  marketingcloudrest: {
    defaults: {},
  },
  netsuitesoap: {
    defaults: {},
  },
};

export function extendConnections(connectionTypes: ConnectionType[]): Connections {
  const newConnections = cloneDeep(defaultConnections);

  connectionTypes.forEach((connectionType) => {
    if (!newConnections[connectionType.type]) {
      return;
    }
    newConnections[connectionType.type].name = connectionType.name;
    newConnections[connectionType.type].description = connectionType.description;
    newConnections[connectionType.type].type = connectionType.type;
    newConnections[connectionType.type].icon_url = connectionType.icon_url;
    newConnections[connectionType.type].auth_data = {};
    newConnections[connectionType.type].button = {
      test: false,
      create: false,
      authenticate: false,
    };
  });

  return newConnections;
}

export function fillGroups(connectionTypes: ConnectionType[]): ConnectionGroup[] {
  const groups: { [key: string]: ConnectionGroup } = {};
  const groupsArray: ConnectionGroup[] = [];
  connectionTypes
    .filter((connectionType) => connectionType.type !== ConnectionTypeName.bigQuery)
    .forEach((connectionType) => {
      connectionType.groups.forEach((group) => {
        if (groups[group.group_type]) {
          groups[group.group_type].connections.push(connectionType);
        } else {
          groups[group.group_type] = {
            name: group.group_name,
            connections: [connectionType],
          };
        }
      });
    });

  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const groupId in groups) {
    groupsArray.push(groups[groupId]);
  }

  return groupsArray.map((group) => ({ ...group, connections: group.connections.sort(sortByName) })).sort(sortByName);
}
